import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ReportCardComponent} from "../../../shared/components/report-card/report-card.component";
import {ReportTableComponent} from "../../dashboard/components/report-table/report-table.component";
import {BootstrapIcon, HeaderService} from "../../../services/header.service";
import {UserService} from "../../../services/user.service";
import {ReportDashboardService} from "../data-access/report-dashboard.service";
import {PieChartComponent} from "../../../shared/components/charts/pie-chart/pie-chart.component";

export interface DashboardItem {
  heading: string,
  name?: string,
  dataSource: any,
  tableHeading: string,
  tableHeadingClass: string,
  displayedColumns: string[],
  columnWidths: ColumnWidths
}

type ColumnWidths = string | { [key: string]: string }

@Component({
  selector: 'app-weekly-dashboard',
  standalone: true,
  imports: [CommonModule, ReportCardComponent, ReportTableComponent, PieChartComponent],
  templateUrl: './weekly-dashboard.component.html',
  styles: [`
    .weekly-report-section {
      margin-bottom: 1rem;
    }

    .dashboard-data:last-of-type {
      pointer-events: none;
    }

    p {
      margin: 0;
    }
  `]
})
export class WeeklyReportDashboardComponent implements OnInit {
  private headerService = inject(HeaderService);
  protected userService = inject(UserService);
  protected reportDashboardService = inject(ReportDashboardService)
  // displayedColumns: ["workplace", "category", "id", "reason"]

  endDate = signal<Date>(new Date(new Date().setDate(new Date().getDate() - 1)));
  startDate = computed(() => {
    const date = new Date(this.endDate());
    date.setDate(date.getDate() - 7);
    return date;
  })

  dashboardItems = computed<Array<DashboardItem>>(() => {
    const vm = this.reportDashboardService.vmWeekly();
    return [
      {
        heading: 'Gelöste Stillstände',
        name: 'solvedReports',
        dataSource: vm.solvedReports,
        tableHeading: "gelöste Stillstände: " + vm.solvedReports.length,
        tableHeadingClass: "text-qundis-primary qundis-ubuntu-32",
        displayedColumns: ["id", "workplace", "category", "reason"],
        columnWidths: 'equal'
      },
      {
        heading: 'Aktive Stillstände',
        name: 'activeReports',
        dataSource: vm.activeReports,
        tableHeading: "aktive Stillstände: " + vm.activeReports.length,
        tableHeadingClass: "text-qundis-warn qundis-ubuntu-32",
        displayedColumns: ["id", "time", "workplace", "category", "reason"],
        columnWidths: {"id": "20%", "time": "10%", "workplace": "30%", "category": "15%", "reason": "25%"}
      },
      {
        heading: "fortlaufende Stillstände",
        dataSource: vm.ongoingActiveReports,
        tableHeading: "fortlaufende aktive Stillstände: " +
          vm.ongoingActiveReports.length,
        tableHeadingClass: "text-qundis-warn qundis-ubuntu-32",
        displayedColumns: ["id", "time", "workplace", "category", "reason"],
        columnWidths: {"id": "20%", "time": "10%", "workplace": "25%", "category": "20%", "reason": "25%"}
      },
      {
        heading: "längste Stillstände",
        dataSource: vm.longestReports!,
        tableHeading: "längste Stillstände: " +
          vm.longestReports?.length,
        tableHeadingClass: "qundis-ubuntu-32",
        displayedColumns: ["id", "time", "duration", "workplace", "category", "reason"],
        columnWidths: {
          "id": "20%",
          "time": "10%",
          "duration": "10%",
          "workplace": "23%",
          "category": "17%",
          "reason": "20%"
        } as { [key: string]: string }
      },
      {
        heading: "häufigste Stillstände",
        dataSource: vm.mostCommonReports!,
        tableHeading: "häufigste Stillstände: " +
          vm.mostCommonReports?.length,
        tableHeadingClass: "qundis-ubuntu-32",
        displayedColumns: ["occurrences", "workplace", "category", "reason"],
        columnWidths: {
          "occurrences": "10%",
          "workplace": "25%",
          "category": "25%",
          "reason": "30%"
        }
      }
    ]
  })

  chartData = computed(() => {
    const vm = this.reportDashboardService.vmWeekly();
    let TOTAL_HOURS = 7 * 24 * 60 * 60 * 1000;
    let standstillTime = vm.solvedReports.reduce((sum, report) => {
      return sum + new Date(report.solved_on!).getTime() - new Date(report.created_at).getTime()
    }, 0)
    return {
      labels: ['Stillstand', 'Betrieb'],
      datasets: [
        {
          label: 'Zeit in h',
          data: [standstillTime / (1000 * 60 * 60), (TOTAL_HOURS - standstillTime) / (1000 * 60 * 60)],
          backgroundColor: [this.getQundisColor('chart-standstill'), this.getQundisColor('chart-idle')],
          hoverOffset: 2
        }
      ]
    }

  })

  constructor() {
    this.headerService.setHeaderTitle('7-Tage-Dashboard');
    this.headerService.setHeaderSubTitle(this.userService.userProductionLineName())
    this.headerService.setHeaderIcon(BootstrapIcon.dailyDashboard);
  }

  ngOnInit() {
    this.reportDashboardService.getWeeklyDashboardData();
  }

  getQundisColor(colorName: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(`--qundis-${colorName}`).trim();
  }

  protected readonly Math = Math;
}
